import { isApiError } from "@sagot/api-client";
import { consoleRoutes, WORKFLOW_ACTIVE_RUN_STATUSES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Dialog, DialogContent, ErrorState, Icon, Sheet, SheetContent, Skeleton, StatusBadge, toast, type StatusKind } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { RunHookBox } from "./run-hook";
import { useCancelRun } from "./use-workflows-p4";
import { useWorkflowRun } from "./use-workflows";

const R = "engage.workflows.runs";
const STEP_STATUS: Record<string, StatusKind> = { done: "success", skipped: "neutral", failed: "danger" };
const isActive = (status: string): boolean => WORKFLOW_ACTIVE_RUN_STATUSES.some((active) => active === status);

function CancelRun({ runId, onDone }: { runId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const cancel = useCancelRun();
  const [asking, setAsking] = useState(false);
  const confirm = () => {
    cancel.mutate(runId, {
      onSuccess: () => { toast.success(t(`${R}.cancelled`)); setAsking(false); onDone(); },
      onError: (error) => {
        // 409: the run ended while the dialog was open; the refreshed detail shows how.
        toast.error(isApiError(error) && error.status === 409 ? t(`${R}.alreadyEnded`) : message(error));
        setAsking(false);
      },
    });
  };
  return (
    <>
      <Button type="button" variant="secondary" size="sm" onClick={() => { setAsking(true); }}>
        <Icon name="square" />
        {t(`${R}.cancel`)}
      </Button>
      <Dialog open={asking} onOpenChange={setAsking}>
        <DialogContent
          title={t(`${R}.cancelTitle`)}
          description={t(`${R}.cancelBody`)}
          closeLabel={t("common.close")}
          footer={
            <>
              <Button variant="secondary" onClick={() => { setAsking(false); }}>{t(`${R}.keepRunning`)}</Button>
              <Button variant="danger" loading={cancel.isPending} onClick={confirm}>{t(`${R}.cancelConfirm`)}</Button>
            </>
          }
        />
      </Dialog>
    </>
  );
}

/** One run: its steps, how it ended, the signed URL while it waits for a webhook, and "Cancel run" while it is active. */
export function RunSheet({ workflowId, runId, onClose }: { workflowId: string; runId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const format = useFormatters();
  const run = useWorkflowRun(workflowId, runId);
  const data = run.data;
  const canCancel = permissions.can("inbox.reply", { anywhere: true }) && !permissions.readOnly;
  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent title={t(`${R}.detailTitle`)} description={data ? format.dateTime(data.started_at) : undefined} closeLabel={t("common.close")} className="max-w-md">
        {run.isPending ? <Skeleton className="h-40" /> : null}
        {run.isError ? <ErrorState headingLevel={3} title={t(`${R}.detailFailed`)} description={t(`${R}.detailFailedBody`)} onRetry={() => void run.refetch()} retryLabel={t("common.retry")} /> : null}
        {data ? (
          <div className="space-y-4">
            {data.error ? <p className="rounded-md bg-danger-soft p-3 text-sm">{t(`${R}.error`)}: <span className="font-mono text-xs">{data.error}</span></p> : null}
            {data.ended_reason ? <p className="text-sm text-fg-muted" data-testid="run-ended-reason">{t(`${R}.endedReason`)}: {t(`${R}.endedReasons.${data.ended_reason}`, { defaultValue: data.ended_reason })}</p> : null}
            {data.parent_run_id ? <p className="text-xs text-fg-muted" data-testid="run-parent">{t(`${R}.calledBy`, { depth: data.depth })}</p> : null}
            {data.goal_met_at ? <Badge tone="success" data-testid="run-goal"><Icon name="target" className="size-3" />{t(`${R}.goalMet`, { when: format.dateTime(data.goal_met_at) })}</Badge> : null}
            {data.hook ? <RunHookBox hook={data.hook} canManage={permissions.can("workflows.manage") && !permissions.readOnly} /> : null}
            <ol className="space-y-3">
              {data.steps.map((step) => (
                <li key={`${step.path}-${step.step_id}`} className="flex items-start gap-3">
                  <StatusBadge status={STEP_STATUS[step.outcome] ?? "neutral"} label={t(`${R}.outcomes.${step.outcome}`, { defaultValue: step.outcome })} />
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold">{t(`engage.workflows.steps.${step.type}`, { defaultValue: step.type })} · {step.path}</p>
                    <p className="truncate font-mono text-xs text-fg-subtle">{step.step_id}{typeof step.detail.reason === "string" ? ` · ${t(`${R}.reasons.${step.detail.reason}`, { defaultValue: step.detail.reason })}` : ""}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap gap-2">
              {data.conversation_id ? <AppLink className="btn btn-secondary btn-sm" href={consoleRoutes.inbox(summary.slug, data.conversation_id)}>{t(`${R}.openConversation`)}</AppLink> : null}
              {data.lead_id ? <AppLink className="btn btn-ghost btn-sm" href={consoleRoutes.leads(summary.slug, data.lead_id)}>{t(`${R}.openLead`)}</AppLink> : null}
              {canCancel && isActive(data.status) ? <CancelRun runId={data.id} onDone={() => void run.refetch()} /> : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
